import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { FuelConsumptionService } from 'src/app/services/fuel-consumption.service';
import { environment } from 'src/environments/environment';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';
import Swal from 'sweetalert2';

interface DayRecord {
    label: string;
    dayOfWeek: number; // 1=Mon ... 7=Sun
    date: Date;
    record: any | null; // existing record from backend
    form: {
        recorded_by: string;
        evidence_date: string;
        fuel_level: number;
        mileage: string;
    };
    dashboardPreviews: string[];
    dashboardFiles: File[];
    tankPreviews: string[];
    tankFiles: File[];
    open: boolean;
    submitted: boolean;
}

@Component({
    selector: 'app-fuel-consumption',
    templateUrl: './fuel-consumption.component.html',
    styleUrls: ['./fuel-consumption.component.css']
})
export class FuelConsumptionComponent implements OnInit {

    readonly isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    unit: any = {};
    dataUser: any = {};
    weekStartDate = '';
    today = '';
    todayDayOfWeek = 0; // 1=Mon ... 7=Sun

    days: DayRecord[] = [];
    historyTab: 'registros' | 'validaciones' = 'registros';

    validateModal: {
        visible: boolean;
        day: DayRecord | null;
        correctlyEvidenced: boolean | null;
        inconsistency: string;
        validatedBy: string;
        submitting: boolean;
    } = {
        visible: false,
        day: null,
        correctlyEvidenced: null,
        inconsistency: '',
        validatedBy: '',
        submitting: false,
    };

    constructor(
        private location: Location,
        private router: Router,
        private spinner: NgxSpinnerService,
        private fuelService: FuelConsumptionService,
        private utils: UtilsService,
        private permissionService: PermissionService
    ) {}

    canDo(action: string): boolean {
        return this.permissionService.canDo('Unidades', action);
    }

    ngOnInit(): void {
        const state: any = this.location.getState();
        this.unit = state?.data;

        if (!this.unit?.id) {
            this.router.navigate(['/unit/list']);
            return;
        }

        this.dataUser = this.utils.getDataUser();
        this.initWeek();
        this.loadRecords();
    }

    private initWeek(): void {
        const now = new Date();
        const jsDay = now.getDay(); // 0=Sun, 1=Mon ... 6=Sat
        // Convert to 1=Mon ... 7=Sun
        this.todayDayOfWeek = jsDay === 0 ? 7 : jsDay;

        // Get Monday of current week
        const monday = new Date(now);
        const diffToMonday = jsDay === 0 ? -6 : 1 - jsDay;
        monday.setDate(now.getDate() + diffToMonday);
        monday.setHours(0, 0, 0, 0);
        this.weekStartDate = this.formatDate(monday);
        this.today = this.formatDate(now);

        const labels = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
        this.days = labels.map((label, i) => {
            const dayNum = i + 1; // 1=Mon ... 7=Sun
            const dayDate = new Date(monday);
            dayDate.setDate(monday.getDate() + i);
            return {
                label,
                dayOfWeek: dayNum,
                date: dayDate,
                record: null,
                form: {
                    recorded_by: this.dataUser?.name ?? '',
                    evidence_date: this.formatDate(dayDate),
                    fuel_level: 0,
                    mileage: ''
                },
                dashboardPreviews: [],
                dashboardFiles: [],
                tankPreviews: [],
                tankFiles: [],
                open: dayNum === this.todayDayOfWeek,
                submitted: false
            };
        });
    }

    private loadRecords(): void {
        this.spinner.show();
        this.fuelService.getByUnit(this.unit.id, this.weekStartDate).subscribe({
            next: (res: any) => {
                this.spinner.hide();
                const records: any[] = Array.isArray(res) ? res : [];
                records.forEach(r => {
                    const day = this.days.find(d => d.dayOfWeek === r.day_of_week);
                    if (day) {
                        day.record = r;
                        day.form.recorded_by = r.recorded_by;
                        day.form.evidence_date = r.evidence_date;
                        day.form.fuel_level = parseFloat(r.fuel_level);
                        day.form.mileage = r.mileage;
                        day.submitted = true;
                    }
                });
            },
            error: () => {
                this.spinner.hide();
            }
        });
    }

    toggleDay(day: DayRecord): void {
        day.open = !day.open;
    }

    isCurrentDay(day: DayRecord): boolean {
        return day.dayOfWeek === this.todayDayOfWeek;
    }

    get currentDay(): DayRecord | undefined {
        return this.days.find(d => d.dayOfWeek === this.todayDayOfWeek);
    }

    get allRecords(): any[] {
        return this.days.filter(d => d.record).map(d => d.record);
    }

    get validatedRecords(): any[] {
        return this.allRecords.filter(r => r.is_valid !== null && r.is_valid !== undefined);
    }

    getNeedleAngle(fuelLevel: number): number {
        // Maps 0→-90deg (E), 50→0deg (½), 100→+90deg (F)
        return (fuelLevel - 50) * 1.8;
    }

    getFuelArcPath(fuelLevel: number): string {
        // Arc from left (20,115) to a point determined by fuelLevel
        // Full arc: A 90 90 0 0 1 200 115 (180° semicircle)
        const cx = 110, cy = 115, r = 90;
        // Angle: 0% = 180° (left), 100% = 0° (right), measured from positive x-axis
        const startAngleDeg = 180;
        const endAngleDeg = 180 - (fuelLevel / 100) * 180;
        const startRad = (startAngleDeg * Math.PI) / 180;
        const endRad = (endAngleDeg * Math.PI) / 180;
        const x1 = cx + r * Math.cos(startRad);
        const y1 = cy - r * Math.sin(startRad); // SVG y-axis is inverted
        const x2 = cx + r * Math.cos(endRad);
        const y2 = cy - r * Math.sin(endRad);
        const largeArc = fuelLevel > 50 ? 1 : 0;
        if (fuelLevel <= 0) return '';
        return `M ${x1.toFixed(1)} ${y1.toFixed(1)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`;
    }

    needleRotation(fuelLevel: number): string {
        // Maps 0 → -90deg (E), 50 → 0deg (½), 100 → 90deg (F)
        const angle = (fuelLevel - 50) * 1.8;
        return `rotate(${angle}deg)`;
    }

    fuelLevelLabel(fuelLevel: number): string {
        if (fuelLevel <= 10) return 'Vacío';
        if (fuelLevel <= 30) return 'Bajo';
        if (fuelLevel <= 60) return 'Medio';
        if (fuelLevel <= 85) return 'Lleno';
        return 'Completo';
    }

    onDashboardFileChange(event: any, day: DayRecord): void {
        this.handleFileChange(event, day, 'dashboard');
    }

    onTankFileChange(event: any, day: DayRecord): void {
        this.handleFileChange(event, day, 'tank');
    }

    private handleFileChange(event: any, day: DayRecord, type: 'dashboard' | 'tank'): void {
        const previews = type === 'dashboard' ? day.dashboardPreviews : day.tankPreviews;
        const files = type === 'dashboard' ? day.dashboardFiles : day.tankFiles;

        const newFiles: File[] = Array.from(event.target.files);
        const max = 4;

        if (previews.length + newFiles.length > max) {
            Swal.fire({ icon: 'warning', text: `Máximo ${max} imágenes permitidas.` });
            event.target.value = '';
            return;
        }

        newFiles.forEach(file => {
            previews.push(URL.createObjectURL(file));
            files.push(file);
        });

        event.target.value = '';
    }

    removeDashboard(day: DayRecord, index: number): void {
        URL.revokeObjectURL(day.dashboardPreviews[index]);
        day.dashboardPreviews.splice(index, 1);
        day.dashboardFiles.splice(index, 1);
    }

    removeTank(day: DayRecord, index: number): void {
        URL.revokeObjectURL(day.tankPreviews[index]);
        day.tankPreviews.splice(index, 1);
        day.tankFiles.splice(index, 1);
    }

    submit(): void {
        const day = this.currentDay;
        if (!day) return;

        if (!day.form.recorded_by.trim()) {
            Swal.fire({ icon: 'warning', text: 'Ingresa el nombre de quien realiza el registro.' });
            return;
        }
        if (!day.form.evidence_date) {
            Swal.fire({ icon: 'warning', text: 'Selecciona la fecha de evidencia.' });
            return;
        }
        if (!day.form.mileage || parseFloat(day.form.mileage) < 0) {
            Swal.fire({ icon: 'warning', text: 'Ingresa el kilometraje actual.' });
            return;
        }
        if (day.dashboardFiles.length === 0 && !day.submitted) {
            Swal.fire({ icon: 'warning', text: 'Sube al menos una foto del tablero de la unidad.' });
            return;
        }

        const payload = {
            unit_id: this.unit.id,
            user_id: this.dataUser.id,
            recorded_by: day.form.recorded_by,
            evidence_date: day.form.evidence_date,
            fuel_level: day.form.fuel_level,
            mileage: day.form.mileage,
            week_start_date: this.weekStartDate,
            day_of_week: day.dayOfWeek
        };

        this.spinner.show();
        this.fuelService.create(payload, day.dashboardFiles, day.tankFiles).subscribe({
            next: (res: any) => {
                this.spinner.hide();
                day.record = res.record;
                day.submitted = true;
                day.dashboardPreviews = [];
                day.dashboardFiles = [];
                day.tankPreviews = [];
                day.tankFiles = [];
                Swal.fire({ icon: 'success', title: 'Éxito', text: 'Registro de consumo guardado correctamente.' });
            },
            error: (err: any) => {
                this.spinner.hide();
                const msg = err?.error ? JSON.stringify(err.error) : 'Error al guardar el registro.';
                Swal.fire({ icon: 'error', text: msg });
            }
        });
    }

    formatDate(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    formatDateDisplay(dateStr: string): string {
        if (!dateStr) return '';
        const [y, m, d] = dateStr.split('-');
        return `${d}/${m}/${y}`;
    }

    getImageUrl(path: string): string {
        return `${environment.url_page}/storage/${path}`;
    }

    hasTankImages(images: any[]): boolean {
        return images.some(img => img.type === 'tank');
    }

    openValidateModal(day: DayRecord): void {
        this.validateModal = {
            visible: true,
            day,
            correctlyEvidenced: null,
            inconsistency: '',
            validatedBy: this.dataUser?.name ?? '',
            submitting: false,
        };
    }

    closeValidateModal(): void {
        this.validateModal.visible = false;
    }

    setValidationAnswer(value: boolean): void {
        this.validateModal.correctlyEvidenced = value;
        if (value) {
            this.validateModal.inconsistency = '';
        }
    }

    confirmValidation(): void {
        const { day, correctlyEvidenced, inconsistency, validatedBy } = this.validateModal;

        if (correctlyEvidenced === null) {
            Swal.fire({ icon: 'warning', text: 'Selecciona si el registro está correctamente evidenciado.' });
            return;
        }
        if (!validatedBy.trim()) {
            Swal.fire({ icon: 'warning', text: 'Ingresa el nombre de quien realiza la validación.' });
            return;
        }
        if (correctlyEvidenced === false && !inconsistency.trim()) {
            Swal.fire({ icon: 'warning', text: 'Ingresa la descripción de la inconsistencia.' });
            return;
        }

        const recordId = day!.record?.id;
        if (!recordId) return;

        this.validateModal.submitting = true;
        this.fuelService.validateRecord(recordId, {
            is_valid: correctlyEvidenced!,
            validation_notes: correctlyEvidenced === false ? inconsistency : undefined,
            validated_by: validatedBy,
        }).subscribe({
            next: (res: any) => {
                this.validateModal.submitting = false;
                day!.record = res.record;
                this.closeValidateModal();
                Swal.fire({ icon: 'success', text: 'Registro validado correctamente.' });
            },
            error: (err: any) => {
                this.validateModal.submitting = false;
                const msg = err?.error ? JSON.stringify(err.error) : 'Error al validar el registro.';
                Swal.fire({ icon: 'error', text: msg });
            }
        });
    }

    getValidationStatus(rec: any): 'pending' | 'valid' | 'invalid' {
        if (rec.is_valid === null || rec.is_valid === undefined) return 'pending';
        return rec.is_valid ? 'valid' : 'invalid';
    }

    formatDateTime(dateStr: string): string {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yy}, ${hh}:${mi}`;
    }

    goBack(): void {
        this.router.navigate(['/unit/list']);
    }
}
